import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { firestore } from "../../config/ConfigFirebase";

// Nombre institucional por defecto cuando config/global.orgName está vacío.
export const DEFAULT_ORG_NAME = "Institución Universitaria Tecnológico de Antioquia";

// General, app-wide settings — stored in config/global and shared by every
// module (PQRSD, Extensión, …). Blank values fall back to env/built-in defaults
// in the consumers. This is the single source of truth for these fields.
export const DEFAULT_GLOBAL_CONFIG = {
  orgName: "",
  brandName: "",
  brandColor: "",
  publicBaseUrl: "",
  logoUrl: "",
  logoWidth: 140,
  // Institutional calendar. `closedDays` are non-working days the law knows
  // nothing about (a closure week, a local fair); `openDays` are days worked
  // in spite of being a holiday or a weekend, and win over everything else.
  // Both are "YYYY-MM-DD" strings and are read by the business-day calculator
  // on the client and in the cloud functions, so every legal term in the app
  // agrees with the door actually being open.
  calendar: { closedDays: [], openDays: [] },
};

// Normalizes what the settings screen sends: valid dates only, de-duplicated,
// sorted, and never the same day in both lists.
export const normalizeCalendar = (raw) => {
  // Shape alone would let "2026-13-45" through, so the date has to be real.
  const isRealDay = (key) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return false;
    const [y, m, d] = key.split("-").map(Number);
    const probe = new Date(Date.UTC(y, m - 1, d));
    return probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
  };
  // A ceiling on the stored list: past this it is a paste accident, and the
  // day-by-day walk in the business-day calculator would crawl.
  const MAX_CALENDAR_DAYS = 2000;
  const clean = (days) => Array.from(new Set(
    (Array.isArray(days) ? days : [])
      .slice(0, MAX_CALENDAR_DAYS)
      .map((d) => String(d || "").trim().slice(0, 10))
      .filter(isRealDay)
  )).sort();
  const openDays = clean(raw?.openDays);
  const open = new Set(openDays);
  // A day declared open cannot also be closed; the explicit working day wins,
  // which is what the calculator does too.
  return { closedDays: clean(raw?.closedDays).filter((d) => !open.has(d)), openDays };
};

// Keys the Global Settings screen owns. Kept explicit so a save never clobbers
// unrelated fields another module might store on config/global.
export const GLOBAL_CONFIG_KEYS = Object.keys(DEFAULT_GLOBAL_CONFIG);

// Reads the shared config/global doc. Public-safe: consumed by the
// unauthenticated postulación pages too. Returns {} on error.
export const getGlobalConfig = async () => {
  try {
    const snap = await getDoc(doc(firestore, "config", "global"));
    return snap.exists() ? snap.data() : {};
  } catch (e) {
    console.error("getGlobalConfig error:", e);
    return {};
  }
};

// Merge-writes the general settings to config/global.
export const saveGlobalConfig = async (patch) => {
  try {
    await setDoc(doc(firestore, "config", "global"), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
    return true;
  } catch (e) {
    console.error("saveGlobalConfig error:", e);
    return false;
  }
};

// Convenience: resolves the institution name with its default fallback.
export const getOrgName = async () => {
  const cfg = await getGlobalConfig();
  return (cfg.orgName || "").trim() || DEFAULT_ORG_NAME;
};
